import React, { useMemo } from 'react'
import { CalendarDateVO } from '../../../../../../../../../../domain/value-object/CalendarDateVO'
import { DatePosition, getPercentXForDate } from '../hooks/useDatePositions'
import { GanttColors, GanttDimensions } from '../constants'
import { Dependency } from './DependencyContext'

interface DependencyLinesProps {
  datePositions: DatePosition[]
  dependencies: Dependency[]
  currentRowIndex: number
  scheduledStart?: CalendarDateVO
  scheduledEnd?: CalendarDateVO
  onLineClick?: (dep: Dependency, event: React.MouseEvent) => void
}

/**
 * DependencyLines - Renders dependency line segments for this row
 *
 * Each row draws its portion of dependency lines:
 * - Source row: Line from bar end to row edge (going down or up to target)
 * - Intermediate row: Vertical line through row
 * - Target row: Line from row edge to bar start with arrow
 */
export const DependencyLines = React.memo<DependencyLinesProps>(
  ({
    datePositions,
    dependencies,
    currentRowIndex,
    scheduledStart,
    scheduledEnd,
    onLineClick,
  }) => {
    // Build elements and collect used colors in a single pass
    const { elements, markerDefs } = useMemo(() => {
      const result: React.ReactNode[] = []
      const usedColors = new Set<string>()

      dependencies.forEach((dep, depIndex) => {
        const handleClick = onLineClick
          ? (e: React.MouseEvent) => onLineClick(dep, e)
          : undefined
        const lineColor = dep.color || GanttColors.DEPENDENCY_LINE
        usedColors.add(lineColor)
        const markerId = `arrow-${currentRowIndex}-${lineColor.replace('#', '')}`
        const depElements = generateDependencyElements(
          dep,
          depIndex,
          datePositions,
          currentRowIndex,
          scheduledStart,
          scheduledEnd,
          markerId,
          handleClick,
          lineColor
        )
        result.push(...depElements)
      })

      // Build marker definitions for each unique color
      const markers = Array.from(usedColors).map(color => (
        <marker
          key={color}
          id={`arrow-${currentRowIndex}-${color.replace('#', '')}`}
          viewBox="0 0 10 10"
          refX="8"
          refY="5"
          markerWidth="4"
          markerHeight="4"
          orient="auto-start-reverse"
        >
          <path
            d="M 0 0 L 8 5 L 0 10"
            fill="none"
            stroke={color}
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </marker>
      ))

      return { elements: result, markerDefs: markers }
    }, [
      datePositions,
      dependencies,
      currentRowIndex,
      scheduledStart,
      scheduledEnd,
      onLineClick,
    ])

    if (currentRowIndex < 0) return null

    return (
      <g className="dependency-lines" strokeLinecap="round">
        <defs>{markerDefs}</defs>
        {elements}
      </g>
    )
  }
)

DependencyLines.displayName = 'DependencyLines'

// ============================================================================
// Helper Types
// ============================================================================

interface ResolvedDates {
  fromEndDate: CalendarDateVO | null
  toStartDate: CalendarDateVO | null
}

type RowRole = 'source' | 'intermediate' | 'target'

// ============================================================================
// Element Generation
// ============================================================================

function generateDependencyElements(
  dependency: Dependency,
  depIndex: number,
  datePositions: DatePosition[],
  currentRowIndex: number,
  scheduledStart?: CalendarDateVO,
  scheduledEnd?: CalendarDateVO,
  markerId?: string,
  onClick?: (e: React.MouseEvent) => void,
  lineColor: string = GanttColors.DEPENDENCY_LINE
): React.ReactNode[] {
  const { fromRowIndex, toRowIndex } = dependency
  const elements: React.ReactNode[] = []

  // Check if this row is involved in this dependency
  const minRow = Math.min(fromRowIndex, toRowIndex)
  const maxRow = Math.max(fromRowIndex, toRowIndex)
  if (currentRowIndex < minRow || currentRowIndex > maxRow) return elements

  // Resolve dates from dependency object and current row's props
  const dates = resolveDependencyDates(
    dependency,
    currentRowIndex,
    scheduledStart,
    scheduledEnd
  )

  // Calculate positions (using percentage)
  const positions = calculatePositions(dates, datePositions)

  const isFromRowAboveToRow = fromRowIndex < toRowIndex
  const rowRole = getRowRole(currentRowIndex, fromRowIndex, toRowIndex)
  const keyPrefix = `dep-${depIndex}`

  const { DEPENDENCY_LINE_WIDTH, DEPENDENCY_CIRCLE_RADIUS } = GanttDimensions

  // Y positions as percentages
  // SVG doesn't support calc(), so we use percentage values only
  // Top: -10% (slightly above row), Bottom: 110% (slightly below row)
  // This extends the lines into adjacent rows
  const barCenterY = '50%'
  const topEdgeY = '-10%'
  const bottomEdgeY = '110%'

  // For detour around bar, offset from center
  const detourOffsetPercent = 30 // 30% from center = above/below bar

  switch (rowRole) {
    case 'source':
      if (positions.fromX !== null && positions.fromExitX !== null) {
        // Draw from bar end, horizontal to exit point, then vertical to edge
        elements.push(
          <g
            key={`${keyPrefix}-source`}
            onClick={onClick}
            style={{ cursor: onClick ? 'pointer' : undefined }}
          >
            {/* Starting circle at bar end */}
            <circle
              cx={`${positions.fromX}%`}
              cy={barCenterY}
              r={DEPENDENCY_CIRCLE_RADIUS}
              fill={lineColor}
            />
            {/* Horizontal line from bar end to exit point */}
            <line
              x1={`${positions.fromX}%`}
              y1={barCenterY}
              x2={`${positions.fromExitX}%`}
              y2={barCenterY}
              stroke={lineColor}
              strokeWidth={DEPENDENCY_LINE_WIDTH}
            />
            {/* Vertical line to row edge */}
            <line
              x1={`${positions.fromExitX}%`}
              y1={barCenterY}
              x2={`${positions.fromExitX}%`}
              y2={isFromRowAboveToRow ? bottomEdgeY : topEdgeY}
              stroke={lineColor}
              strokeWidth={DEPENDENCY_LINE_WIDTH}
            />
            {/* Transparent hit areas for easier right-click */}
            <line
              x1={`${positions.fromX}%`}
              y1={barCenterY}
              x2={`${positions.fromExitX}%`}
              y2={barCenterY}
              stroke="transparent"
              strokeWidth={12}
              style={{ pointerEvents: 'stroke' }}
            />
            <line
              x1={`${positions.fromExitX}%`}
              y1={barCenterY}
              x2={`${positions.fromExitX}%`}
              y2={isFromRowAboveToRow ? bottomEdgeY : topEdgeY}
              stroke="transparent"
              strokeWidth={12}
              style={{ pointerEvents: 'stroke' }}
            />
          </g>
        )
      }
      break

    case 'intermediate':
      if (positions.fromExitX !== null) {
        // Draw vertical line through entire row
        elements.push(
          <g
            key={`${keyPrefix}-intermediate`}
            onClick={onClick}
            style={{ cursor: onClick ? 'pointer' : undefined }}
          >
            <line
              x1={`${positions.fromExitX}%`}
              y1={topEdgeY}
              x2={`${positions.fromExitX}%`}
              y2={bottomEdgeY}
              stroke={lineColor}
              strokeWidth={DEPENDENCY_LINE_WIDTH}
            />
            {/* Transparent hit area */}
            <line
              x1={`${positions.fromExitX}%`}
              y1={topEdgeY}
              x2={`${positions.fromExitX}%`}
              y2={bottomEdgeY}
              stroke="transparent"
              strokeWidth={12}
              style={{ pointerEvents: 'stroke' }}
            />
          </g>
        )
      }
      break

    case 'target':
      if (positions.toX !== null) {
        const entryY = isFromRowAboveToRow ? topEdgeY : bottomEdgeY
        const detourY = isFromRowAboveToRow
          ? `${50 - detourOffsetPercent}%`
          : `${50 + detourOffsetPercent}%`

        if (positions.fromExitX !== null && positions.toEntryX !== null) {
          // Use detour path when:
          // 1. Dates are reversed (source end is after target start)
          // 2. Positions cross due to offsets (adjacent dates where fromExitX >= toX)
          // This ensures the arrow always arrives from the LEFT, pointing RIGHT toward the bar
          const needsDetour =
            isReversedDateOrder(dates) || positions.fromExitX >= positions.toX

          elements.push(
            <g
              key={`${keyPrefix}-target`}
              onClick={onClick}
              style={{ cursor: onClick ? 'pointer' : undefined }}
            >
              {needsDetour ? (
                // Detour path: vertical → horizontal detour → vertical → horizontal arrow
                // The final segment always goes left→right so the arrow points at the bar
                <>
                  {/* Vertical from edge to detour level */}
                  <line
                    x1={`${positions.fromExitX}%`}
                    y1={entryY}
                    x2={`${positions.fromExitX}%`}
                    y2={detourY}
                    stroke={lineColor}
                    strokeWidth={DEPENDENCY_LINE_WIDTH}
                  />
                  {/* Horizontal detour */}
                  <line
                    x1={`${positions.fromExitX}%`}
                    y1={detourY}
                    x2={`${positions.toEntryX}%`}
                    y2={detourY}
                    stroke={lineColor}
                    strokeWidth={DEPENDENCY_LINE_WIDTH}
                  />
                  {/* Vertical from detour to center */}
                  <line
                    x1={`${positions.toEntryX}%`}
                    y1={detourY}
                    x2={`${positions.toEntryX}%`}
                    y2={barCenterY}
                    stroke={lineColor}
                    strokeWidth={DEPENDENCY_LINE_WIDTH}
                  />
                  {/* Horizontal to bar start with arrow (left→right) */}
                  <line
                    x1={`${positions.toEntryX}%`}
                    y1={barCenterY}
                    x2={`${positions.toX}%`}
                    y2={barCenterY}
                    stroke={lineColor}
                    strokeWidth={DEPENDENCY_LINE_WIDTH}
                    markerEnd={markerId ? `url(#${markerId})` : undefined}
                  />
                  {/* Transparent hit areas */}
                  <line
                    x1={`${positions.fromExitX}%`}
                    y1={entryY}
                    x2={`${positions.fromExitX}%`}
                    y2={detourY}
                    stroke="transparent"
                    strokeWidth={12}
                    style={{ pointerEvents: 'stroke' }}
                  />
                  <line
                    x1={`${positions.fromExitX}%`}
                    y1={detourY}
                    x2={`${positions.toEntryX}%`}
                    y2={detourY}
                    stroke="transparent"
                    strokeWidth={12}
                    style={{ pointerEvents: 'stroke' }}
                  />
                  <line
                    x1={`${positions.toEntryX}%`}
                    y1={detourY}
                    x2={`${positions.toEntryX}%`}
                    y2={barCenterY}
                    stroke="transparent"
                    strokeWidth={12}
                    style={{ pointerEvents: 'stroke' }}
                  />
                  <line
                    x1={`${positions.toEntryX}%`}
                    y1={barCenterY}
                    x2={`${positions.toX}%`}
                    y2={barCenterY}
                    stroke="transparent"
                    strokeWidth={12}
                    style={{ pointerEvents: 'stroke' }}
                  />
                </>
              ) : (
                // Normal case: direct path (fromExitX is to the left of toX)
                // Arrow goes left→right, so marker correctly points at the bar
                <>
                  {/* Vertical from edge to center */}
                  <line
                    x1={`${positions.fromExitX}%`}
                    y1={entryY}
                    x2={`${positions.fromExitX}%`}
                    y2={barCenterY}
                    stroke={lineColor}
                    strokeWidth={DEPENDENCY_LINE_WIDTH}
                  />
                  {/* Horizontal to bar start with arrow */}
                  <line
                    x1={`${positions.fromExitX}%`}
                    y1={barCenterY}
                    x2={`${positions.toX}%`}
                    y2={barCenterY}
                    stroke={lineColor}
                    strokeWidth={DEPENDENCY_LINE_WIDTH}
                    markerEnd={markerId ? `url(#${markerId})` : undefined}
                  />
                  {/* Transparent hit areas */}
                  <line
                    x1={`${positions.fromExitX}%`}
                    y1={entryY}
                    x2={`${positions.fromExitX}%`}
                    y2={barCenterY}
                    stroke="transparent"
                    strokeWidth={12}
                    style={{ pointerEvents: 'stroke' }}
                  />
                  <line
                    x1={`${positions.fromExitX}%`}
                    y1={barCenterY}
                    x2={`${positions.toX}%`}
                    y2={barCenterY}
                    stroke="transparent"
                    strokeWidth={12}
                    style={{ pointerEvents: 'stroke' }}
                  />
                </>
              )}
            </g>
          )
        }
      }
      break
  }

  return elements
}

// ============================================================================
// Date Resolution
// ============================================================================

function resolveDependencyDates(
  dependency: Dependency,
  currentRowIndex: number,
  scheduledStart?: CalendarDateVO,
  scheduledEnd?: CalendarDateVO
): ResolvedDates {
  const { fromRowIndex, toRowIndex } = dependency

  // For the current row, use props (CalendarDateVO); for other rows, use embedded dates
  const fromEndDate: CalendarDateVO | null =
    currentRowIndex === fromRowIndex
      ? (scheduledEnd ?? null)
      : dependency.fromEndDate
        ? CalendarDateVO.of(dependency.fromEndDate)
        : null

  const toStartDate: CalendarDateVO | null =
    currentRowIndex === toRowIndex
      ? (scheduledStart ?? null)
      : dependency.toStartDate
        ? CalendarDateVO.of(dependency.toStartDate)
        : null

  return { fromEndDate, toStartDate }
}

// ============================================================================
// Position Calculation
// ============================================================================

interface DependencyPositions {
  fromX: number | null
  toX: number | null
  fromExitX: number | null
  toEntryX: number | null
}

function calculatePositions(
  dates: ResolvedDates,
  datePositions: DatePosition[]
): DependencyPositions {
  // Get percentage positions for dates
  const fromX = dates.fromEndDate
    ? getPercentXForDate(datePositions, dates.fromEndDate, 'right')
    : null
  const toX = dates.toStartDate
    ? getPercentXForDate(datePositions, dates.toStartDate, 'left')
    : null

  // Offset for vertical line exit/entry points
  const offsetPercent = 1.5

  return {
    fromX,
    toX,
    fromExitX: fromX !== null ? fromX + offsetPercent : null,
    toEntryX: toX !== null ? toX - offsetPercent : null,
  }
}

// ============================================================================
// Utility Functions
// ============================================================================

function getRowRole(
  currentRowIndex: number,
  fromRowIndex: number,
  toRowIndex: number
): RowRole {
  if (currentRowIndex === fromRowIndex) return 'source'
  if (currentRowIndex === toRowIndex) return 'target'
  return 'intermediate'
}

function isReversedDateOrder(dates: ResolvedDates): boolean {
  const { fromEndDate, toStartDate } = dates
  if (!fromEndDate || !toStartDate) return false
  return fromEndDate.isAfter(toStartDate) || fromEndDate.isEqual(toStartDate)
}
